import { useEffect, useState } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppInsightsContext, AppInsightsErrorBoundary, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { ErrorComponent } from '@vivli/shared/components';
import { IContextWrapper } from '@vivli/shared/infrastructure/interface';
import { environment } from '@vivli/core/environments';
import { useAppConfigContext } from '@vivli/core/infrastructure/context';

export const ErrorBoundaryWrapper = ({ children }: IContextWrapper) => {
    const [reactPlugin, setReactPlugin] = useState<ReactPlugin>();
    const config = useAppConfigContext();

    const setupAppInsights = (insightsConnString: string) => {
        // skip app insights for local development
        if (!insightsConnString || environment.isLocal) {
            return;
        }

        const browserHistory = createBrowserHistory();
        const reactPlugin = new ReactPlugin();
        const appInsights = new ApplicationInsights({
            config: {
                connectionString: insightsConnString,
                extensions: [reactPlugin],
                extensionConfig: {
                    [reactPlugin.identifier]: { history: browserHistory },
                },
                enableRequestHeaderTracking: true,
                enableResponseHeaderTracking: true,
                enableAjaxErrorStatusText: true,
                enableAjaxPerfTracking: true,
                enableAutoRouteTracking: true,
                enableUnhandledPromiseRejectionTracking: true,
                autoTrackPageVisitTime: true,
            },
        });

        appInsights.loadAppInsights();
        appInsights.trackPageView();

        setReactPlugin(reactPlugin);
    };

    useEffect(() => {
        if (config?.insightsConnString) {
            setupAppInsights(config.insightsConnString);
        }
    }, [config]);

    // skip app insights for local development
    if (environment.isLocal) {
        return children;
    }

    if (!reactPlugin) {
        return <div></div>;
    }

    return (
        reactPlugin && (
            <AppInsightsErrorBoundary onError={() => <ErrorComponent />} appInsights={reactPlugin}>
                <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>
            </AppInsightsErrorBoundary>
        )
    );
};
